/* ============================================================
   SOCIAL SPELL — MAIN JAVASCRIPT
   Vanilla JS: nav, animations, floating CTA, page transitions
   Process wheel: IntersectionObserver + scroll-reactive, no hijacking
   ============================================================ */

(function () {
  'use strict';

  /* ── DOM Ready ────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    initPageTransition();
    initAnnouncementBar();
    initHeader();
    initMobileNav();
    initHeroAnimations();
    initScrollAnimations();
    initFloatingCTA();
    initServiceAccordions();
    initEmailForms();
    initProcessWheel();
  });

  /* ── Page Transition ──────────────────────────────────────── */
  function initPageTransition() {
    var overlay = document.querySelector('.page-transition-overlay');
    if (!overlay) return;

    requestAnimationFrame(function () {
      overlay.style.opacity = '0';
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;
      var isExternal = link.hostname && link.hostname !== window.location.hostname;
      var isHash = href.startsWith('#');
      var isSpecial = href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:');
      if (isExternal || isHash || isSpecial) return;
      e.preventDefault();
      overlay.classList.add('active');
      setTimeout(function () { window.location.href = href; }, 300);
    });

    window.addEventListener('pageshow', function () {
      overlay.classList.remove('active');
    });
  }

  /* ── Announcement Bar ─────────────────────────────────────── */
  function initAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar');
    var closeBtn = document.querySelector('.announcement-bar__close');
    if (!bar || !closeBtn) return;

    if (sessionStorage.getItem('ss-announcement-dismissed') === '1') {
      bar.classList.add('is-hidden');
      return;
    }

    setAnnouncementOffset(bar);

    closeBtn.addEventListener('click', function () {
      bar.classList.add('is-hidden');
      sessionStorage.setItem('ss-announcement-dismissed', '1');
      document.documentElement.style.setProperty('--announcement-height', '0px');
    });

    window.addEventListener('resize', function () { setAnnouncementOffset(bar); });
  }

  function setAnnouncementOffset(bar) {
    if (!bar || bar.classList.contains('is-hidden')) return;
    var height = bar.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--announcement-height', height + 'px');
  }

  /* ── Header Scroll Behaviour ──────────────────────────────── */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var scrollThreshold = 60;
    var ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          if (window.scrollY > scrollThreshold) {
            header.classList.add('is-scrolled');
          } else {
            header.classList.remove('is-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    var currentPath = window.location.pathname;
    document.querySelectorAll('.site-header__nav-link').forEach(function (link) {
      if (link.getAttribute('href') === currentPath) link.classList.add('is-active');
    });
  }

  /* ── Mobile Navigation ────────────────────────────────────── */
  function initMobileNav() {
    var hamburger = document.querySelector('.site-header__hamburger');
    var mobileNav = document.querySelector('.mobile-nav');
    var closeBtn = document.querySelector('.mobile-nav__close');
    if (!hamburger || !mobileNav) return;

    function openNav() {
      mobileNav.classList.add('is-open');
      hamburger.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    function closeNav() {
      mobileNav.classList.remove('is-open');
      hamburger.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    hamburger.addEventListener('click', function () {
      if (mobileNav.classList.contains('is-open')) closeNav(); else openNav();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeNav);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeNav(); });

    var currentPath = window.location.pathname;
    document.querySelectorAll('.mobile-nav__link').forEach(function (link) {
      if (link.getAttribute('href') === currentPath) link.classList.add('is-active');
    });
  }

  /* ── Hero Animations ──────────────────────────────────────── */
  function initHeroAnimations() {
    var headline = document.querySelector('.hero__headline');
    if (!headline) return;

    var text = headline.innerHTML;
    var words = text.split(' ');
    headline.innerHTML = words.map(function (word) {
      return '<span class="word">' + word + '</span>';
    }).join(' ');

    var wordEls = headline.querySelectorAll('.word');
    var baseDelay = 100;

    wordEls.forEach(function (word, i) {
      setTimeout(function () { word.classList.add('is-visible'); }, baseDelay + (i * 80));
    });

    var subheadline = document.querySelector('.hero__subheadline');
    if (subheadline) {
      setTimeout(function () {
        subheadline.classList.add('is-visible');
      }, baseDelay + (wordEls.length * 80) + 200);
    }

    var buttonsEl = document.querySelector('.hero__buttons');
    if (buttonsEl) {
      setTimeout(function () {
        buttonsEl.classList.add('is-visible');
      }, baseDelay + (wordEls.length * 80) + 600);
    }

    var heroImage = document.querySelector('.hero__image');
    if (heroImage) {
      setTimeout(function () { heroImage.classList.add('is-visible'); }, 400);
    }
  }

  /* ── Scroll Animations via IntersectionObserver ───────────── */
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.fade-up, .fade-in, .scale-in').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observerOptions = {
      root: null,
      rootMargin: '0px 0px -60px 0px',
      threshold: 0.1
    };

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-up, .fade-in, .scale-in').forEach(function (el) {
      observer.observe(el);
    });

    var staggerGroups = document.querySelectorAll('.stagger-children');
    var staggerObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var children = entry.target.querySelectorAll('.fade-up, .fade-in, .scale-in');
          children.forEach(function (child, i) {
            setTimeout(function () { child.classList.add('is-visible'); }, i * 120);
          });
          staggerObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    staggerGroups.forEach(function (group) { staggerObserver.observe(group); });
  }

  /* ── Floating CTA Visibility ──────────────────────────────── */
  function initFloatingCTA() {
    var floatingCTA = document.querySelector('.floating-cta');
    var hero = document.querySelector('.hero');
    if (!floatingCTA) return;

    var threshold = hero ? hero.offsetHeight : window.innerHeight * 0.8;
    var ticking = false;

    function checkVisibility() {
      if (!ticking) {
        requestAnimationFrame(function () {
          if (window.scrollY > threshold) {
            floatingCTA.classList.add('is-visible');
          } else {
            floatingCTA.classList.remove('is-visible');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', checkVisibility, { passive: true });
    window.addEventListener('resize', function () {
      threshold = hero ? hero.offsetHeight : window.innerHeight * 0.8;
    });
  }

  /* ── Service Accordions ───────────────────────────────────── */
  function initServiceAccordions() {
    document.querySelectorAll('details.service-item').forEach(function (details) {
      var summary = details.querySelector('summary');
      var body = details.querySelector('.service-item__body');
      if (!summary || !body) return;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (details.open) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () {
            body.style.height = '0';
            body.style.overflow = 'hidden';
          });
          setTimeout(function () {
            details.open = false;
            body.style.height = '';
            body.style.overflow = '';
          }, 300);
        } else {
          details.open = true;
          body.style.height = '0';
          body.style.overflow = 'hidden';
          requestAnimationFrame(function () { body.style.height = body.scrollHeight + 'px'; });
          setTimeout(function () {
            body.style.height = '';
            body.style.overflow = '';
          }, 300);
        }
      });
    });
  }

  /* ── Email Form Submissions ───────────────────────────────── */
  function initEmailForms() {
    document.querySelectorAll('.email-signup__form').forEach(function (form) {
      form.addEventListener('submit', function () {
        var btn = form.querySelector('.btn');
        if (btn) { btn.textContent = "You're in! ✨"; btn.disabled = true; }
      });
    });
  }

  /* ── Process Wheel ────────────────────────────────────────── */
  /* Natural scroll — no hijacking. Reads position reactively.  */
  function initProcessWheel() {
    var section = document.getElementById('process-section');
    if (!section) return;

    var arc         = document.getElementById('process-arc');
    var centerEl    = document.getElementById('process-center');
    var centerNum   = document.getElementById('process-center-num');
    var centerTitle = document.getElementById('process-center-title');
    var centerDesc  = document.getElementById('process-center-desc');
    var circumference = 1256.64; /* 2π × r200 */

    var steps = [
      { num: '01', title: 'Inquire',               desc: 'Tell us about your brand and what you\'re working toward. We\'ll take it from there.' },
      { num: '02', title: 'Discovery Call',         desc: 'A free 15-minute call to understand your goals, your vision and which service is the right fit.' },
      { num: '03', title: 'Proposal & Onboarding',  desc: 'We send your custom proposal, you say yes, and we get you set up with everything we need to begin.' },
      { num: '04', title: 'Strategy & Direction',   desc: 'Before anything is created, we get the thinking right. Strategy, positioning, creative direction — all locked in.' },
      { num: '05', title: 'Create & Deliver',       desc: 'The magic happens. We build, design and craft your deliverables to the highest standard.' },
      { num: '06', title: 'Review & Refine',        desc: 'Two rounds of revisions to make sure everything feels exactly right before we hand it over.' }
    ];

    var activeStep = -1;
    var ticking = false;
    var transitionTimeout = null;

    /* ── Reactive scroll progress — NO locking ──────────────── */
    /* Progress 0→1 as the section travels from entering viewport
       (top at 80% down screen) to exiting (bottom at 20% from top). */
    function getProgress() {
      var rect = section.getBoundingClientRect();
      var vh = window.innerHeight;
      var entered = (vh * 0.80) - rect.top;       /* pixels since section entered */
      var total   = rect.height + (vh * 0.60);    /* total travel window */
      return Math.max(0, Math.min(1, entered / total));
    }

    function activateStep(stepIndex) {
      if (stepIndex === activeStep) return;
      activeStep = stepIndex;

      var s = steps[stepIndex];

      /* Crossfade center content */
      if (centerEl) {
        centerEl.classList.add('is-transitioning');
        clearTimeout(transitionTimeout);
        transitionTimeout = setTimeout(function () {
          if (centerNum)   centerNum.textContent   = s.num;
          if (centerTitle) centerTitle.textContent = s.title;
          if (centerDesc)  centerDesc.textContent  = s.desc;
          centerEl.classList.remove('is-transitioning');
        }, 220);
      }

      /* Update dots and pulse rings */
      for (var i = 1; i <= 6; i++) {
        var dot      = document.getElementById('dot-' + i);
        var pulse    = document.getElementById('pulse-' + i);
        var labelNum = document.getElementById('label-num-' + i);
        var lblName  = document.getElementById('label-name-' + i);
        var isActive = (i === stepIndex + 1);
        var isPast   = (i < stepIndex + 1);

        if (isActive || isPast) {
          if (dot)      dot.setAttribute('fill', '#a9b896');
          if (labelNum) labelNum.setAttribute('fill', '#a9b896');
          if (lblName)  lblName.setAttribute('fill', '#a9b896');
        } else {
          if (dot)      dot.setAttribute('fill', '#d5d5d7');
          if (labelNum) labelNum.removeAttribute('fill');
          if (lblName)  lblName.removeAttribute('fill');
        }
        if (pulse) pulse.setAttribute('opacity', isActive ? '1' : '0');
      }
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          var progress = getProgress();

          /* Draw arc continuously — smooth, just reacting to scroll */
          if (arc) {
            arc.style.strokeDashoffset = (circumference * (1 - progress)).toFixed(2);
          }

          /* Activate step based on progress */
          var stepIndex = Math.min(5, Math.floor(progress * 6));
          activateStep(stepIndex);

          ticking = false;
        });
        ticking = true;
      }
    }

    /* Set initial state */
    activateStep(0);

    /* Listen to scroll reactively — no scroll locking anywhere */
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); /* Fire once in case section already in view */

    /* Also activate mobile timeline dots via IntersectionObserver */
    var mobileItems = document.querySelectorAll('.process-mobile__item');
    if (mobileItems.length && 'IntersectionObserver' in window) {
      var mobileObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-active');
          }
        });
      }, { threshold: 0.5 });

      mobileItems.forEach(function (item) { mobileObserver.observe(item); });
    }
  }

})();
